import type { ReactNode } from 'react';
import { COLORS } from '../../constants/theme';
import { ratioWidthPercent } from './HubCard';

/**
 * SF-132: shell primitive for a percent-of-total line item with a tap-to-expand panel and a
 * strategy-target progress bar.
 *
 * SF-190: collapsed row redesigned around a Now/Target peer layout (replacing the old
 * pct+absolute+target-pill row) and a percent-of-land bar with a target notch (replacing the old
 * ratio-to-target fill bar, which saturated identically at exactly-at-target and 3x-target).
 *
 * Post-SF-190 follow-up: the bar's fill is no longer a single red/yellow/green
 * target-proximity color (`progressColor()`, now dead code and removed). It's a segmented
 * composition bar instead — built / in-progress / pending — so it shows what a value is made of,
 * not a verdict on how close it is to target. See `computeBarSegments()` below.
 */
export interface PercentRowProps {
  label: string;
  pct: number;
  absolute: number;
  targetPct: number | null;
  inProgressPct?: number | null;
  /** SF-143: unsubmitted, page-local draft (buildQty) — distinct from inProgressPct's
   *  already-submitted, server-side construction. Cosmetic sub-label only; does not feed the
   *  progress-bar ratio below (a draft is reversible and not yet spent).
   *  SF-165: signed — positive means a queued Build draft (`"+N% planned"`, `COLORS.link`),
   *  negative means a queued Raze draft (`"N% razing"`, `COLORS.negative`, the number's own minus
   *  sign supplies the `−`). `null`/`0` (including `Math.round()`-produced `-0`, since neither
   *  `-0 > 0` nor `-0 < 0` hold in JS) renders no sub-label. */
  draftPct?: number | null;
  expanded: boolean;
  onToggle: () => void;
  disabled?: boolean;
  children?: ReactNode;
}

function progressBucket(ratio: number | null): 'green' | 'yellow' | 'red' | 'none' {
  if (ratio === null) return 'none';
  if (ratio >= 1.0) return 'green';
  if (ratio >= 0.5) return 'yellow';
  return 'red';
}

/** SF-165: reworded to be direction-neutral — "on track"/"behind" implies climbing toward a
 *  target you're below, which reads oddly on a Raze row where the green state means "you have
 *  surplus to raze" (you're above, not below, the target). Works identically for Build and Raze
 *  since both are fundamentally "how does current position compare to target." Still used for the
 *  bar's accessible label post-SF-190-follow-up even though the bar itself no longer colors by
 *  this bucket — "approaching target" remains a meaningful spoken description independent of the
 *  segmented built/in-progress/pending visual encoding. */
function progressStateLabel(bucket: 'green' | 'yellow' | 'red' | 'none', targetPct: number | null): string {
  switch (bucket) {
    case 'green':
      return `At or above ${targetPct}% target`;
    case 'yellow':
      return `Approaching ${targetPct}% target`;
    case 'red':
      return `Below ${targetPct}% target`;
    case 'none':
    default:
      return 'No target set';
  }
}

interface BarSegment {
  key: string;
  color: string;
  left: number;
  width: number;
}

/**
 * Post-SF-190 follow-up: composition bar segments, in commitment order — built (owned) closest
 * to zero, a local/reversible draft (least committed) furthest out. `left`/`width` are raw
 * percent-of-land values; `width` gets the SF-167 min-visible-width floor per segment
 * (`ratioWidthPercent`) so a real-but-tiny segment isn't an imperceptible sliver — `left` stays
 * unfloored, so an extreme edge case (several tiny segments) can overlap slightly rather than
 * distort the larger, common case.
 *
 * Build direction (`draftPct >= 0` or null): built, then in-progress, then a pending (drafted)
 * addition — each appended past the previous segment's raw (unfloored) end.
 *
 * Raze direction (`draftPct < 0`): mirrored, not appended — a queued raze draft carves a pending
 * (yellow) slice off the TOP of what's owned (nearest the current edge), so "built" only covers
 * what would remain after the queued raze completes. In-progress construction is unrelated to the
 * raze ceiling (a different building's build queue can run concurrently) and still just tacks on
 * past the owned edge, informationally.
 */
function computeBarSegments(pct: number, inProgressPct: number, draftPct: number): BarSegment[] {
  const segments: BarSegment[] = [];
  if (draftPct < 0) {
    const magnitude = Math.min(-draftPct, pct);
    const builtWidth = Math.max(0, pct - magnitude);
    if (builtWidth > 0) {
      segments.push({ key: 'built', color: COLORS.progressBuilt, left: 0, width: ratioWidthPercent(builtWidth, 100) });
    }
    if (magnitude > 0) {
      segments.push({ key: 'pending', color: COLORS.progressYellow, left: builtWidth, width: ratioWidthPercent(magnitude, 100) });
    }
    if (inProgressPct > 0) {
      segments.push({ key: 'inprogress', color: COLORS.progressInProgress, left: pct, width: ratioWidthPercent(inProgressPct, 100) });
    }
  } else {
    if (pct > 0) {
      segments.push({ key: 'built', color: COLORS.progressBuilt, left: 0, width: ratioWidthPercent(pct, 100) });
    }
    if (inProgressPct > 0) {
      segments.push({ key: 'inprogress', color: COLORS.progressInProgress, left: pct, width: ratioWidthPercent(inProgressPct, 100) });
    }
    if (draftPct > 0) {
      segments.push({ key: 'pending', color: COLORS.progressYellow, left: pct + inProgressPct, width: ratioWidthPercent(draftPct, 100) });
    }
  }
  return segments;
}

export function PercentRow({
  label,
  pct,
  absolute,
  targetPct,
  inProgressPct,
  draftPct,
  expanded,
  onToggle,
  disabled,
  children,
}: PercentRowProps) {
  const panelId = `percent-row-panel-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const hasTarget = targetPct !== null && targetPct > 0;
  const ratio = hasTarget ? (pct + (inProgressPct ?? 0)) / (targetPct as number) : null;
  const bucket = progressBucket(ratio);
  const stateLabel = progressStateLabel(bucket, targetPct);
  const segments = computeBarSegments(pct, inProgressPct ?? 0, draftPct ?? 0);
  // SF-165: three-way, not the old binary hasQueuedDraft ? link : primary — a negative draftPct
  // (queued Raze) gets its own distinct color, matching the sub-label's color branching below.
  const draftColor = draftPct != null && draftPct > 0
    ? COLORS.link
    : draftPct != null && draftPct < 0
      ? COLORS.negative
      : COLORS.text.primary;

  return (
    <div className="border-b border-game-700 last:border-b-0">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
        disabled={disabled}
        className="w-full flex items-center gap-2.5 text-left"
        style={{ minHeight: 44, padding: '11px 12px 4px', background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer' }}
      >
        <div className="flex-1 min-w-0">
          <span className="text-sm truncate block" style={{ color: COLORS.text.primary }}>
            {label}
          </span>
          {inProgressPct != null && inProgressPct > 0 && (
            <span className="text-[10px] block" style={{ color: COLORS.gold }}>
              +{inProgressPct}% in progress
            </span>
          )}
          {draftPct != null && draftPct > 0 && (
            <span className="text-[10px] block" style={{ color: COLORS.link }}>
              +{draftPct}% planned
            </span>
          )}
          {draftPct != null && draftPct < 0 && (
            <span className="text-[10px] block" style={{ color: COLORS.negative }}>
              {draftPct}% razing
            </span>
          )}
        </div>
        {/* Post-SF-190 follow-up: Now/Target share one grid instead of two independently-centered
            flex blocks — the 3-line Now block (caption/value/acres) and the 2-line Target block
            (caption/value) previously centered as separate units, so their captions/values landed
            at different heights whenever Now's extra acres line was present. A shared grid keeps
            every line type in the same row regardless of which column has more content. */}
        <div
          className="grid gap-x-3 flex-shrink-0 text-right"
          style={{ gridTemplateColumns: '48px 44px' }}
        >
          <span className="text-[9px] uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
            Now
          </span>
          <span className="text-[9px] uppercase tracking-wide" style={{ color: COLORS.text.tertiary }}>
            Target
          </span>
          <span className="font-mono text-base font-bold" style={{ color: draftColor }}>
            {Math.round(pct)}%
          </span>
          <span
            className="font-mono text-base font-bold"
            style={{ color: hasTarget ? COLORS.text.primary : COLORS.text.tertiary }}
          >
            {hasTarget ? `${targetPct}%` : '—'}
          </span>
          <span className="font-mono text-[10px]" style={{ color: COLORS.text.tertiary }}>
            {absolute} ac
          </span>
          <span />
        </div>
        <span aria-hidden="true" className="text-xs flex-shrink-0" style={{ color: COLORS.text.tertiary, width: 12, textAlign: 'center' }}>
          {expanded ? '▴' : '▾'}
        </span>
      </button>

      <div className="px-3 pb-2.5">
        <div
          data-progress={bucket}
          role="img"
          aria-label={stateLabel}
          className="rounded-full relative"
          style={{ height: 6, backgroundColor: COLORS.bg }}
        >
          {segments.map((seg, i) => (
            <div
              key={seg.key}
              data-segment={seg.key}
              className="absolute inset-y-0"
              style={{
                left: `${seg.left}%`,
                width: `${seg.width}%`,
                backgroundColor: seg.color,
                borderTopLeftRadius: i === 0 ? 999 : 0,
                borderBottomLeftRadius: i === 0 ? 999 : 0,
                borderTopRightRadius: i === segments.length - 1 ? 999 : 0,
                borderBottomRightRadius: i === segments.length - 1 ? 999 : 0,
              }}
            />
          ))}
          {hasTarget && (
            <div
              aria-hidden="true"
              className="absolute top-0 bottom-0"
              style={{
                left: `${Math.min(100, Math.max(0, targetPct as number))}%`,
                width: 2,
                backgroundColor: COLORS.text.primary,
                borderRadius: 1,
              }}
            />
          )}
        </div>
      </div>

      {expanded && (
        <div id={panelId} className="px-3 pb-3.5 flex flex-col gap-2.5">
          {children}
        </div>
      )}
    </div>
  );
}
